import {
  FieldWrapper,
  Input,
  Label,
  Textarea,
  useToggle,
} from '@commenda/kakashi';
import {
  TSysAgentStepAttachment,
  TSysAgentStepDefinition,
  TSysAgentStepFormResponseSource,
  TValueProvider,
} from '@commenda/types';
import { useState } from 'react';
import { ServiceAuditorStepHelper } from '../../../workflow/helpers/ServiceAuditorStepHelper';
import { extractUrlVariables } from '../../../workflow/workflow-templates/validation';
import { useWorkflow } from '../../context/WorkflowContext';
import {
  TPromptKey,
  TPromptMappingKey,
  TSelectedEntity,
} from '../../types/node';
import { VariablesBindingModal } from '../modals/VariablesBindingModal';
import { AgentCustomInstructionsField } from './AgentCustomInstructionsField';
import { AgentDocumentCategoryField } from './AgentDocumentCategoryField';
import { AgentFormResponsesField } from './AgentFormResponsesField';
import { AgentProfileField } from './AgentProfileField';
import {
  AgentPromptVariablesField,
  bindingsForMappingKey,
} from './AgentPromptVariablesField';
import { AgentStepResourcesField } from './AgentStepResourcesField';
import { ServiceAuditorNodePropertyPanel } from './ServiceAuditorNodePropertyPanel';
import { useIsAgentDocumentConfigurable } from './useIsAgentDocumentConfigurable';

export const AgentNodePropertyPanel = ({
  selectedEntity,
}: {
  selectedEntity: TSelectedEntity;
}) => {
  const { workflow, setWorkflow, updateStep } = useWorkflow();
  const isDocumentConfigurable = useIsAgentDocumentConfigurable();
  const bindingModalVisibility = useToggle(false);
  const [editingVariable, setEditingVariable] = useState<{
    variable: string;
    mappingKey: TPromptMappingKey;
  } | null>(null);

  const selectedStep = workflow?.steps.find(
    (step) => step.id === selectedEntity.id
  ) as TSysAgentStepDefinition | undefined;

  if (!selectedStep) return <div>Step not found</div>;

  // The auditor's prompt and frame are code, so its panel shows the data it reads instead of prompt fields.
  if (ServiceAuditorStepHelper.isServiceAuditorStep(selectedStep)) {
    return (
      <ServiceAuditorNodePropertyPanel
        workflow={workflow}
        auditorStep={selectedStep}
        onStepChange={(updates) => updateStep(selectedEntity.id, updates)}
      />
    );
  }

  const updateAgentStep = (updates: Partial<TSysAgentStepDefinition>) => {
    setWorkflow({
      ...workflow,
      steps: workflow?.steps.map((step) =>
        step.id === selectedEntity.id ? { ...step, ...updates } : step
      ),
    });
  };

  const bindingsFor = (mappingKey: TPromptMappingKey) =>
    bindingsForMappingKey(selectedStep, mappingKey);

  // Prunes bindings for variables the author removed, so a stale entry cannot outlive the text that used it.
  const updatePrompt = (
    key: TPromptKey,
    mappingKey: TPromptMappingKey,
    text: string
  ) => {
    const existing = bindingsFor(mappingKey);
    const kept: Record<string, TValueProvider> = {};
    extractUrlVariables(text).forEach((variable) => {
      if (existing[variable]) kept[variable] = existing[variable];
    });
    updateAgentStep({ [key]: text, [mappingKey]: kept });
  };

  // `readOnly` narrows this step's profile to its read tier and drives the canvas write badge. What the run
  // may reach at all is decided by the profile it names, not by this flag.
  const canWrite = selectedStep.readOnly === false;
  const hasTask = !!selectedStep.userPrompt?.trim();

  return (
    <div className="flex flex-col gap-xl">
      <FieldWrapper>
        <Label>What this step can do</Label>
        <p className="txt-extra-small-regular text-text-secondary">
          {isDocumentConfigurable
            ? canWrite
              ? "May change this company's data, within whatever the expert below is granted."
              : "Reads this company's data only. It cannot make changes."
            : canWrite
              ? "Reads and edits this company's data, using whichever tools it needs."
              : "Reads this company's data. It cannot make changes."}
        </p>
        {!hasTask && (
          <p className="txt-extra-small-regular text-text-error mt-xs">
            Add a task below so this step knows what to do.
          </p>
        )}
      </FieldWrapper>

      {isDocumentConfigurable && (
        <AgentProfileField
          profileKey={selectedStep.profileKey}
          enabled={isDocumentConfigurable}
          onChange={(profileKey) =>
            // A discrete pick, so it goes through updateStep and is undoable. Nothing else is
            // cleared: custom instructions are authored prose that must survive a profile change.
            updateStep(selectedEntity.id, { profileKey })
          }
        />
      )}

      <AgentFormResponsesField
        workflow={workflow}
        agentStepId={selectedEntity.id}
        source={selectedStep.formResponseSource}
        onChange={(formResponseSource: TSysAgentStepFormResponseSource) =>
          // updateStep rather than the panel's own setWorkflow: a discrete pick should be undoable, and only
          // updateStep checkpoints. The prompt textareas stay on setWorkflow, where a per-keystroke checkpoint
          // would be worse than none.
          updateStep(selectedEntity.id, { formResponseSource })
        }
      />

      <FieldWrapper>
        <Label optional>System prompt</Label>
        <Textarea
          placeholder="Standing instructions for the agent..."
          value={selectedStep.systemPrompt || ''}
          onChange={(e) =>
            updatePrompt(
              'systemPrompt',
              'systemPromptValueMapping',
              e.target.value
            )
          }
          rows={3}
        />
        {isDocumentConfigurable && (
          <p className="txt-extra-small-regular text-text-secondary mt-xs">
            <strong>Replaces</strong>&nbsp;the expert&apos;s own instructions
            entirely. To add to them instead, use the extra instructions below.
          </p>
        )}
      </FieldWrapper>

      {isDocumentConfigurable && (
        <AgentCustomInstructionsField
          customInstructions={selectedStep.customInstructions}
          onChange={(customInstructions) =>
            updateAgentStep({ customInstructions })
          }
        />
      )}

      <FieldWrapper>
        <Label>Task</Label>
        <Textarea
          placeholder="Describe what this step should do, e.g. Summarise the {{rowCount}} rows from the previous step..."
          value={selectedStep.userPrompt || ''}
          onChange={(e) =>
            updatePrompt('userPrompt', 'userPromptValueMapping', e.target.value)
          }
          rows={5}
        />
        <p className="txt-extra-small-regular text-text-secondary mt-xs">
          The step runs against the company this workflow is running for. Its
          tools are curated in the builder chat. Write {'{{variable}}'} to pull
          in another step&apos;s output.
        </p>
      </FieldWrapper>

      <AgentPromptVariablesField
        selectedStep={selectedStep}
        onVariableEdit={(variable, mappingKey) => {
          setEditingVariable({ variable, mappingKey });
          bindingModalVisibility.on();
        }}
      />

      {isDocumentConfigurable && (
        <AgentStepResourcesField
          resourceText={selectedStep.resourceText}
          attachments={selectedStep.attachments ?? []}
          onResourceTextChange={(resourceText) =>
            updateAgentStep({ resourceText })
          }
          onAttachmentsChange={(
            attachments: TSysAgentStepAttachment[] | undefined
          ) =>
            // Discrete, so undoable — and empty is written as undefined.
            updateStep(selectedEntity.id, { attachments })
          }
        />
      )}

      {isDocumentConfigurable && (
        <AgentDocumentCategoryField
          documentTemplateCategoryKey={selectedStep.documentTemplateCategoryKey}
          enabled={isDocumentConfigurable}
          onChange={(documentTemplateCategoryKey) =>
            updateStep(selectedEntity.id, { documentTemplateCategoryKey })
          }
        />
      )}

      <FieldWrapper>
        <Label>Model</Label>
        <Input
          value={selectedStep.model || 'Agentic (platform default)'}
          disabled
          className="bg-white"
        />
        <p className="txt-extra-small-regular text-text-secondary mt-xs">
          This step always runs as an agent. The model and mode are managed by
          the platform.
        </p>
      </FieldWrapper>

      <FieldWrapper>
        <Label>Guardrails</Label>
        <p className="txt-extra-small-regular text-text-secondary">
          The platform caps the number of turns the expert may take — the cap
          depends on the expert this step runs as — and stops the run when it is
          reached. There is no time limit on a step and no cost ceiling: a
          run&apos;s cost is recorded, not capped.
        </p>
      </FieldWrapper>

      {workflow && (
        <VariablesBindingModal
          isOpen={bindingModalVisibility.visible}
          onClose={() => {
            bindingModalVisibility.off();
            setEditingVariable(null);
          }}
          variableName={editingVariable?.variable ?? null}
          value={
            editingVariable
              ? (bindingsFor(editingVariable.mappingKey)[
                  editingVariable.variable
                ] ?? null)
              : null
          }
          onSave={(variableName, value) => {
            if (!editingVariable) return;
            updateAgentStep({
              [editingVariable.mappingKey]: {
                ...bindingsFor(editingVariable.mappingKey),
                [variableName]: value,
              },
            });
          }}
          workflowDefinition={workflow}
          currentStepId={selectedEntity.id}
        />
      )}
    </div>
  );
};
